// components/user/UpgradeModal.tsx
import React, { useState, useEffect } from "react";
import Modal from "../../components/ui/Modal";
import Button from "../../components/ui/Button";
import Select from "../../components/ui/Select";
import Input from "../../components/ui/Input";
import LoadingIndicator from "../../components/ui/LoadingIndicator";
import ErrorMessage from "../../components/ui/ErrorMessage";
import { previewCoupon } from "../../lib/apiClient";
import { getErrorMessage } from "../../lib/utils";
import { CouponPreviewResponse, UserSubscription, Plan } from "../../types";
interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentSubscription: UserSubscription;
  upgradeOptions: Plan[];
  onConfirmUpgrade: (selectedNewPlanId: string, iscombo: boolean, couponCode?: string) => void;
  isLoadingOptions: boolean;
  isLoadingConfirm: boolean;
  error?: string | null;
}
const UpgradeModal: React.FC<UpgradeModalProps> = ({
  isOpen,
  onClose,
  currentSubscription,
  upgradeOptions,
  onConfirmUpgrade,
  isLoadingOptions,
  isLoadingConfirm,
  error,
}) => {
  const [selectedPlanId, setSelectedPlanId] = useState<string>("");
  const [couponCode, setCouponCode] = useState<string>("");
  const [pricingPreview, setPricingPreview] = useState<CouponPreviewResponse | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  useEffect(() => {
    if (isOpen) {
      setSelectedPlanId("");
      setCouponCode("");
      setPricingPreview(null);
      setPreviewError(null);
    }
  }, [isOpen]);

  useEffect(() => {
    const normalizedCoupon = couponCode.trim().toUpperCase();

    if (!isOpen || !selectedPlanId || !normalizedCoupon) {
      setPricingPreview(null);
      setPreviewError(null);
      setIsPreviewLoading(false);
      return;
    }

    let cancelled = false;

    const runPreview = async () => {
      setIsPreviewLoading(true);
      setPreviewError(null);
      try {
        const pricing = await previewCoupon({
          couponCode: normalizedCoupon,
          plan_id: selectedPlanId,
          isCombo: false,
          condition: "upgrade",
        });
        if (!cancelled) {
          setPricingPreview(pricing);
        }
      } catch (err) {
        if (!cancelled) {
          setPricingPreview(null);
          setPreviewError(getErrorMessage(err));
        }
      } finally {
        if (!cancelled) {
          setIsPreviewLoading(false);
        }
      }
    };

    runPreview();

    return () => {
      cancelled = true;
    };
  }, [couponCode, isOpen, selectedPlanId]);

  const handleConfirm = () => {
    if (selectedPlanId) {
      onConfirmUpgrade(selectedPlanId, false, couponCode.trim() || undefined);
    }
  };
  const planSelectOptions = upgradeOptions.map((plan) => ({
    value: plan._id,
    label: `${plan.name} - ₹${plan.discounted_price ?? plan.markup_price} (${plan.validity_days
      } days)`,
  }));
  const currentPlan =
    typeof currentSubscription.plan_id === "object"
      ? currentSubscription.plan_id
      : null;
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Extend Subscription: ${typeof currentSubscription.channel_id === "object"
          ? currentSubscription.channel_id.name
          : "Channel"
        }`}
      size="md"
    >
      <div className="space-y-6">
        <div className="bg-dark-tertiary p-3 rounded text-sm border border-dark-border">
          <p className="text-text-secondary mb-1">
            Current Plan:{" "}
            <span className="font-medium text-text-primary">
              {currentPlan?.name || "N/A"}
            </span>
            {currentPlan &&
              ` (₹${currentPlan.discounted_price ?? currentPlan.markup_price
              }, ${currentPlan.validity_days} days)`}
          </p>
        </div>
        {isLoadingOptions && (
          <div className="flex justify-center py-4">
            <LoadingIndicator text="Loading available extend plans..." />
          </div>
        )}
        {!isLoadingOptions && error && (
          <ErrorMessage message={error} className="my-2" />
        )}
        {!isLoadingOptions && !error && upgradeOptions.length === 0 && (
          <p className="text-center text-text-secondary italic py-4">
            No plans available for upgrade at this time.
          </p>
        )}
        {!isLoadingOptions && !error && upgradeOptions.length > 0 && (
          <>
            <Select
              label="Select New Plan to Extend To"
              options={planSelectOptions}
              value={selectedPlanId}
              onChange={(e) => setSelectedPlanId(e.target.value)}
              placeholder="-- Choose an plan --"
              disabled={isLoadingConfirm}
              required
              name="new_plan_id"
            />

            <Input
              label="Coupon Code (Optional)"
              name="couponCode"
              value={couponCode}
              onChange={(e) => setCouponCode(String(e.target.value || "").toUpperCase())}
              placeholder="ENTERCOUPON"
              disabled={isLoadingConfirm}
              error={previewError}
            />

            {isPreviewLoading ? (
              <div className="rounded-md border border-dark-border bg-dark-primary/40 px-3 py-2 text-sm text-text-secondary">
                Checking coupon...
              </div>
            ) : null}

            {pricingPreview ? (
              <div className="rounded-md border border-functional-success/30 bg-functional-success/10 px-3 py-3 text-sm">
                <div className="flex items-center justify-between text-text-secondary">
                  <span>Base price</span>
                  <span>₹{pricingPreview.baseAmount}</span>
                </div>
                <div className="flex items-center justify-between text-functional-success mt-1">
                  <span>Discount ({pricingPreview.discountPercentage}%)</span>
                  <span>-₹{pricingPreview.discountAmount}</span>
                </div>
                <div className="flex items-center justify-between text-text-primary font-semibold mt-2 pt-2 border-t border-functional-success/20">
                  <span>Final price</span>
                  <span>₹{pricingPreview.finalAmount}</span>
                </div>
              </div>
            ) : null}
          </>
        )}
        <div className="flex justify-end space-x-3 pt-6 border-t border-dark-tertiary">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={isLoadingConfirm}
          >
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={handleConfirm}
            isLoading={isLoadingConfirm}
            disabled={
              isLoadingOptions ||
              isLoadingConfirm ||
              isPreviewLoading ||
              (!!couponCode.trim() && !!previewError) ||
              !selectedPlanId ||
              upgradeOptions.length === 0
            }
          >
            Confirm & Proceed
          </Button>
        </div>
      </div>
    </Modal>
  );
};
export default UpgradeModal;
